import React, { useEffect, useState, useCallback } from 'react';
import { useGlobalState } from './GlobalStateProvider';

export default function WorkoutLibrary() {
  const gs = useGlobalState() as any;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    /* mount */
  }, []);

  // Safe toast helper (guards missing dependency)
  async function notify(kind: 'success' | 'error' | 'info', msg: string) {
    try {
      const m = await import('sonner');
      const fn = (m.toast as any)?.[kind] || (m.toast as any);
      fn?.(msg);
    } catch {
      try {
        console[kind === 'error' ? 'error' : 'log']('[toast]', msg);
      } catch {
        /* ignored */
      }
    }
  }

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await gs.listCoachWorkouts?.(search || '');
      setRows(Array.isArray(list) ? list : []);
    } catch (e: any) {
      notify('error', e?.message || 'Failed to load workouts');
    } finally {
      setLoading(false);
    }
  }, [gs, search]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Listen for workouts:changed to refresh after saves
  useEffect(() => {
    const onChanged = () => reload();
    window.addEventListener('workouts:changed', onChanged);
    return () => window.removeEventListener('workouts:changed', onChanged);
  }, [reload]);

  const onCreate = async () => {
    try {
      const w = await gs.saveCoachWorkout(
        {
          name: `New Workout ${new Date().toLocaleTimeString()}`,
          modality: 'strength',
          difficulty: 'beginner',
          tags: [],
        },
        [],
      );
      if (!w?.id) throw new Error('Create returned no id');
      notify('success', 'Workout created');
      await reload();
    } catch (e: any) {
      notify('error', e?.message || 'Failed to create');
    }
  };

  const onRename = async (id: string, newName: string) => {
    try {
      const cur = await gs.getWorkout(id);
      const saved = await gs.saveCoachWorkout({ id, name: newName }, cur?.items || []);
      if (!saved?.id) throw new Error('Save returned no id');
      notify('success', 'Workout saved');
      await reload();
    } catch (e: any) {
      notify('error', e?.message || 'Failed to save');
    }
  };

  const onDuplicate = async (id: string) => {
    try {
      const dup = await gs.duplicateCoachWorkout(id);
      if (!dup?.id) throw new Error('Duplicate returned no id');
      notify('success', 'Duplicated');
      await reload();
    } catch (e: any) {
      notify('error', e?.message || 'Failed to duplicate');
    }
  };

  const onDelete = async (id: string) => {
    try {
      const ok = await gs.deleteCoachWorkout(id);
      if (!ok) throw new Error('Delete returned false');
      notify('success', 'Moved to trash');
      await reload();
    } catch (e: any) {
      notify('error', e?.message || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-4">
      <div data-test="which-workouts" style={{ display: 'none' }}>
        WorkoutLibrary
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          reload();
        }}
        className="flex items-center gap-2"
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search workouts…"
          className="w-full rounded border px-3 py-2 bg-background"
        />
        <button
          type="button"
          className="rounded bg-foreground text-background px-3 py-2 text-sm"
          disabled={loading}
          onClick={onCreate}
        >
          New Workout
        </button>
      </form>

      {loading ? (
        <div className="opacity-60">Loading workouts…</div>
      ) : rows.length === 0 ? (
        <div className="opacity-60">No workouts yet.</div>
      ) : (
        <div className="grid gap-3">
          {rows.map((w: any) => (
            <div key={w.id} className="rounded-lg border p-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-medium truncate">{w.name}</div>
                <div className="text-xs opacity-70">
                  {w.modality || '—'} · {typeof w.itemsCount === 'number' ? w.itemsCount : 0} items
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded border text-sm"
                  disabled={loading}
                  onClick={() => {
                    const next = window.prompt('Rename workout', w.name);
                    if (next && next !== w.name) onRename(w.id, next);
                  }}
                >
                  Rename
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded border text-sm"
                  disabled={loading}
                  onClick={() => onDuplicate(w.id)}
                >
                  Duplicate
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded border text-sm text-red-600"
                  disabled={loading}
                  onClick={() => {
                    if (window.confirm(`Move "${w.name}" to trash?`)) onDelete(w.id);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
